<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_LOANS_AND_ADVANCES_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="8"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <!-- Status -->
            <b-col md="3">
              <SelectField
                id="status"
                v-model="filter.reportType"
                label=""
                :placeholder="$t('Select')"
                :options="translatedAccountStatusOptions"
                :set-selected="getReportType"
                item="label"
                :reduce="option => option.value"
              />
            </b-col>
            <!-- Status -->
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="loanAdvancesTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(linkedAccountBalance)="data">
        <p class="mb-0">
          {{ formatAmount(data.value) }}
        </p>
      </template>

      <template #cell(startDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(endDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(disbursedAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(refinanceAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(totalOutstandingAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(accruingInterestReceivable)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalDueAndUnpaid)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalNotYetDue)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(interestDueAndUnpaid)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(lastLodgmentDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(disbursementDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(installmentAmount)="data">
        <p class="mb-0">
          {{ data.value ? formatAmount(data.value) : '' }}
        </p>
      </template>
      <template #cell(loanStatus)="data">
        <div v-if="data.value">
          <b-badge
            pill
            :variant="getStatusColor(data.value)"
          >
            {{ data.value }}
          </b-badge>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="loanAdvanceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter account number')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->

            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter account name')"
                type="text"
              />
            </b-col>
            <!-- Account Name -->
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
              />
            </b-col>
            <!-- IPPIS/Oracle ID -->

            <b-col md="6 mt-2">
              <SwitchButton
                v-model="filter.useCurrentFinancialDate"
                :label="$t('Use Current Financial Date')"
                name="use-current-financial-date"
                :readonly="isReadonly"
                @input="setUseCurrentFinancialDate(filter.useCurrentFinancialDate)"
              />
            </b-col>

            <!-- Financial Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date"
                v-model="filter.financialDate"
                :min-date="new Date()"
                :label="$t('Financial Date')"
                :config="datePickerConfig"
                rules="required"
                :error-name="$t('Financial Date')"
                :placeholder="$t('Select financial date')"
                :disabled="isDisabledText"
              />
            </b-col>
            <!-- Financial Date -->

            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->

            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <b-col md="6">
              <InputField
                id="employee-name"
                v-model="filter.employeeName"
                :label="$t('Employee Name')"
                :placeholder="$t('Enter Employee Name')"
                type="text"
              />
            </b-col>
            <b-col md="6">
              <DatePickerField
                id="disbursement-date-from"
                v-model="filter.disbursementDateFrom"
                :label="$t('Disbursement Date From')"
                :config="datePickerConfigOthers"
                :placeholder="$t('Select date')"
              />
            </b-col>
            <b-col md="6">
              <DatePickerField
                id="disbursement-date-to"
                v-model="filter.disbursementDateTo"
                :label="$t('Disbursement Date To')"
                :config="datePickerConfigOthers"
                :placeholder="$t('Select date')"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="filter.accountOfficerStaffId"
                :label="$t('Account Officer')"
                :options="accountOfficerOptions"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- modal -->
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner,
  BModal, BDropdown, BDropdownItem, BBadge, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { mapActions, mapGetters } from 'vuex'
import { datePickerConfig } from '@core/mixins/datePicker'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import getStatusColor from '../../../../../helpers/getStatusColor'
import toNumber from '../../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate } from '../../../../../helpers/formatDate'
import SwitchButton from '../../../../components/switch/Switch.vue'
import Button from '../../../../components/button/Button.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import { reportPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BForm,
    InputField,
    BModal,
    BBadge,
    BDropdown,
    BDropdownItem,
    TablePagination,
    TableEntries,
    SelectField,
    DatePickerField,
    SwitchButton,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, datePickerConfig, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabledText: false,
      query: '',
      getStatusColor,
      statusToggleLabel: '',
      isReadonly: false,
      isDisabled: true,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Loan and Advances Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'linkedAccount', sortable: true, label: 'Linked Account' },
        { key: 'linkedAccountBalance', sortable: true, label: 'Linked Account Balance' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'product', sortable: true, label: 'Product' },
        { key: 'productCategory', sortable: true, label: 'Product Category' },
        { key: 'economicSector', sortable: true, label: 'Economic Sector' },
        { key: 'lendingModel', sortable: true, label: 'Lending Model' },
        { key: 'tenure', sortable: true, label: 'Tenure' },
        { key: 'startDate', sortable: true, label: 'Start Date' },
        { key: 'endDate', sortable: true, label: 'End Date' },
        { key: 'disbursedAmount', sortable: true, label: 'Disbursed Amount' },
        { key: 'disbursementDate', sortable: true, label: 'Disbursed Date' },
        { key: 'refinanceAmount', sortable: true, label: 'Refinanced Amount' },
        { key: 'interestRate', sortable: true, label: 'Int. Rate (PA)' },
        { key: 'defaultingLoanInterest', sortable: true, label: 'Defaulting Loan Interest' },
        { key: 'totalOutstandingAmount', sortable: true, label: 'Total Outstanding Principal' },
        { key: 'principalDueAndUnpaid', sortable: true, label: 'Principal Due & Unpaid' },
        { key: 'principalNotYetDue', sortable: true, label: 'Principal Not Yet Due' },
        { key: 'interestDueAndUnpaid', sortable: true, label: 'Interest Due & Unpaid' },
        { key: 'accruingInterestReceivable', sortable: true, label: 'Accruing Interest Recievable' },
        { key: 'daysPastDue', sortable: true, label: 'Days Past Due' },
        { key: 'lastRepaymentAmount', sortable: true, label: 'Last Repaid Amount' },
        { key: 'installmentAmount', sortable: true, label: 'Installment Amount' },
        { key: 'employerName', sortable: true, label: 'Employer Name' },
        { key: 'employerAddress', sortable: true, label: 'Employer Address' },
        { key: 'lastLodgmentDate', sortable: true, label: 'Last Lodgment Date' },
        { key: 'lastRepaymentDate', sortable: true, label: 'Last Repaid Date' },
        { key: 'phoneNumber', sortable: true, label: 'Cust. Phone No' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'guarantorName', sortable: true, label: 'Guarantor Name' },
        { key: 'guarantorPhoneNumber', sortable: true, label: 'Guarantor Phone Number' },
        { key: 'loanStatus', sortable: true, label: 'Status' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        financialDate: null,
        productCode: '',
        branchCode: '',
        employeeID: '',
        employerName: '',
        accountOfficerStaffId: '',
        disbursementDateFrom: null,
        disbursementDateTo: null,
        useCurrentFinancialDate: true,
        reportType: 1,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      datePickerConfigOthers: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      accountStatusOptions: [
        { value: 1, label: 'Loan' },
        { value: 2, label: 'Overdraft' },
      ],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      productOptions: [],
      accountOfficerOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedAccountStatusOptions() {
      return this.accountStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanAndAdvances()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanAndAdvances()
    },
  },
  mounted() {
    this.fetchProduct()
    this.fetchLoanAndAdvances()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getLoanAdvances: 'reports/getLoanAdvances',
      downloadLoanAdvances: 'reports/downloadLoanAdvances',
      getProductByTypes: 'account/getProductByTypes',
      getBranchByCode: 'operations/getBranchByCode',
      getAccountOfficers: 'account/getAccountOfficerList',
    }),

    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_AND_ADVANCES_REPORT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
      this.fetchLoanAndAdvances()
      this.filter.reportType = 1
    },
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabledText = false
      } else {
        this.isDisabledText = true
        this.filter.financialDate = null
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProductByTypes({
          type1: 'Loan',
          type2: 'Savings',
          permission: reportPermissions.LOAN_AND_ADVANCES_REPORT,
        })
        const { data } = response
        if (data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.loanAdvanceRules.validate().then(async success => {
          if (success) {
            this.fetchLoanAndAdvances()
          }
        })
      } else {
        this.fetchLoanAndAdvances()
      }
    },
    async fetchLoanAndAdvances() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getLoanAdvances({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isDisabled = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async getReportType(type) {
      if (type) {
        this.filter.reportType = type
        await this.fetchLoanAndAdvances()
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadLoanAdvances({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_LOANS_AND_ADVANCES_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your ',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadLoanAdvances({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_LOANS_AND_ADVANCES_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: 'Your download is in progress and might take a little longer. Go to your',
            link: {
              text: 'Download Manager',
              route: { name: 'download-manager' },
            },
            afterLinkText: ' to access the completed file.',
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
